import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Iconify from 'src/components/Iconify';
import { Button } from 'src/@/components/ui/button';
import { Card } from 'src/@/components/ui/card';
import { Dialog, DialogContent } from 'src/@/components/ui/dialog';
import useAuth from 'src/hooks/useAuth';
import { useToast } from 'src/@/components/ui/use-toast';
import propostasService from 'src/services/propostasService';
import CanvasReadOnly from './editor/canvas/CanvasReadOnly';
import DocumentCanvas from './editor/DocumentCanvas';
import { CATEGORIAS_MODELO, MODELOS_PRE_PRONTOS, getCategoriaLabel } from './modelosPreProntosData';

// Converte um modelo (do catálogo ou salvo) numa proposta de exemplo só para a
// pré-visualização — dados fictícios de contato para as variáveis renderizarem.
function propostaDePreview(modelo) {
  return {
    titulo: modelo.nome,
    layout: modelo.layout,
    modo_design: modelo.modo_design || 'blocos',
    canvas: modelo.canvas,
    blocos: modelo.blocos || [],
    itens: modelo.itens_padrao || [],
    desconto_global_percent: 0,
    validade_dias: modelo.validade_dias_default || 15,
    contato: { nome: 'Cliente Exemplo', email: 'cliente@exemplo.com', telefone: '(31) 99999-0000' },
    created_at: new Date().toISOString(),
  };
}

// Miniatura fiel ao layout real de cada estilo de modelo: 'hero' (faixa escura),
// 'minimal' (barra fina + régua grossa) e 'faixa' (filete lateral).
function MiniPreview({ temaCor, logoUrl, estilo }) {
  const tema = temaCor || '#7C3AED';
  const corpo = (
    <div className="p-2 space-y-1" style={{ backgroundColor: estilo === 'minimal' ? '#ffffff' : `${tema}08` }}>
      <div className="flex items-center gap-1">
        {estilo === 'faixa'
          ? <div className="w-0.5 h-2 rounded-sm flex-shrink-0" style={{ backgroundColor: tema }} />
          : estilo === 'minimal'
            ? null
            : <div className="w-2 h-2 rounded-[3px] flex-shrink-0" style={{ backgroundColor: tema }} />}
        <div className={`h-1.5 rounded w-1/2 ${estilo === 'minimal' ? 'border-b' : ''}`} style={{ backgroundColor: `${tema}55`, borderColor: tema }} />
      </div>
      <div className="h-1 bg-gray-200 rounded w-full" />
      <div className="h-1 bg-gray-200 rounded w-5/6" />
      <div className="h-3 rounded-sm bg-gray-50 border-l-2" style={{ borderColor: tema }} />
    </div>
  );
  return (
    <div className="h-28 dark:bg-[#1A1D21] flex items-center justify-center p-3" style={{ backgroundColor: `${tema}0D` }}>
      <div className="w-full max-w-[150px] rounded-md shadow-sm overflow-hidden border border-gray-200 bg-white relative">
        {estilo === 'faixa' && <div className="absolute left-0 top-0 bottom-0 w-[3px]" style={{ backgroundColor: tema }} />}
        {estilo === 'minimal' ? (
          <>
            <div className="h-[3px]" style={{ backgroundColor: tema }} />
            <div className="px-2 pt-1.5 space-y-0.5">
              <div className="h-2 w-20 rounded-sm bg-gray-900" />
              <div className="h-[2px] w-full bg-gray-900" />
            </div>
          </>
        ) : estilo === 'faixa' ? (
          <div className="px-2.5 pt-1.5 space-y-0.5">
            <div className="h-2 w-20 rounded-sm bg-gray-900" />
            <div className="h-[3px] w-6 rounded-sm" style={{ backgroundColor: tema }} />
          </div>
        ) : estilo === 'blocado' ? (
          <div className="px-2 py-2" style={{ backgroundColor: tema }}>
            <div className="h-2 w-20 rounded-sm bg-white/95" />
            <div className="h-1 w-12 rounded-sm bg-white/50 mt-0.5" />
          </div>
        ) : estilo === 'central' ? (
          <div className="px-2 pt-2 flex flex-col items-center gap-0.5">
            <div className="h-2 w-20 rounded-sm bg-gray-900" />
            <div className="h-[3px] w-5 rounded-sm" style={{ backgroundColor: tema }} />
          </div>
        ) : estilo === 'geometrico' ? (
          <div className="px-2 pt-2 relative">
            <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full" style={{ backgroundColor: tema }} />
            <div className="absolute top-3 right-5 w-3 h-3 rounded-full" style={{ backgroundColor: tema, opacity: 0.3 }} />
            <div className="h-2 w-16 rounded-sm bg-gray-900" />
            <div className="h-[3px] w-6 rounded-sm mt-0.5" style={{ backgroundColor: tema }} />
          </div>
        ) : (
          <>
            <div className="px-2 py-1.5 space-y-0.5" style={{ backgroundColor: '#0f172a' }}>
              {logoUrl
                ? <img src={logoUrl} alt="" className="h-2 object-contain self-start" />
                : <div className="h-1.5 w-16 rounded-sm bg-white/90" />}
              <div className="h-1 w-10 rounded-sm bg-white/40" />
            </div>
            <div className="h-[3px]" style={{ backgroundColor: tema }} />
          </>
        )}
        {corpo}
      </div>
    </div>
  );
}

function CategoriaBadge({ categoria }) {
  return (
    <span className="text-[9px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-md bg-gray-100 text-gray-500 dark:bg-[#1A1D21] dark:text-[#ABABAD] flex-shrink-0">
      {getCategoriaLabel(categoria)}
    </span>
  );
}

export default function ModelosList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const isAdmin = ['admin', 'root'].includes(user?.level);

  // Modelo em pré-visualização (dialog com o documento renderizado em tamanho real)
  const [previewModelo, setPreviewModelo] = useState(null);

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoriaFiltro, setCategoriaFiltro] = useState('todas');

  useEffect(() => {
    if (user?.clienteId) loadTemplates();
  }, [user?.clienteId]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await propostasService.listarTemplates(user?.clienteId);
      if (!response.error) setTemplates(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar modelos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUsarProprio = (template) => {
    navigate(`/dashboard/propostas/nova?template=${template._id}`);
  };

  const handleUsarCatalogo = (modelo) => {
    navigate(`/dashboard/propostas/nova?catalogo=${modelo.id}`);
  };

  const handleDuplicar = async (template) => {
    try {
      const response = await propostasService.criarTemplate({
        cliente_id: user?.clienteId,
        nome: `${template.nome} (cópia)`,
        descricao: template.descricao,
        categoria: template.categoria,
        layout: template.layout,
        blocos: template.blocos,
        modo_design: template.modo_design,
        canvas: template.canvas,
        itens_padrao: template.itens_padrao,
        validade_dias_default: template.validade_dias_default,
        config_automacao: template.config_automacao
      });
      if (response.error) throw new Error(response.message);
      toast({ title: 'Sucesso', description: 'Modelo duplicado' });
      loadTemplates();
    } catch (error) {
      toast({ title: 'Erro', description: error.message || 'Não foi possível duplicar o modelo', variant: 'destructive' });
    }
  };

  const handleSalvarNaBiblioteca = async (modelo) => {
    try {
      const response = await propostasService.criarTemplate({
        cliente_id: user?.clienteId,
        nome: modelo.nome,
        descricao: modelo.descricao,
        categoria: modelo.categoria,
        layout: modelo.layout,
        blocos: modelo.blocos,
        modo_design: modelo.modo_design,
        canvas: modelo.canvas,
        itens_padrao: modelo.itens_padrao,
        validade_dias_default: modelo.validade_dias_default
      });
      if (response.error) throw new Error(response.message);
      toast({ title: 'Sucesso', description: 'Modelo salvo nos seus modelos' });
      loadTemplates();
    } catch (error) {
      toast({ title: 'Erro', description: error.message || 'Não foi possível salvar o modelo', variant: 'destructive' });
    }
  };

  const handleExcluir = async (template) => {
    if (!window.confirm(`Excluir o modelo "${template.nome}"?`)) return;
    try {
      const response = await propostasService.deletarTemplate(template._id);
      if (response.error) throw new Error(response.message);
      setTemplates(prev => prev.filter(t => t._id !== template._id));
      toast({ title: 'Sucesso', description: 'Modelo excluído' });
    } catch (error) {
      toast({ title: 'Erro', description: error.message || 'Não foi possível excluir o modelo', variant: 'destructive' });
    }
  };

  const categoriasDisponiveis = useMemo(() => {
    const usadas = new Set(['geral']);
    templates.forEach(t => usadas.add(t.categoria || 'geral'));
    MODELOS_PRE_PRONTOS.forEach(m => usadas.add(m.categoria));
    return CATEGORIAS_MODELO.filter(c => usadas.has(c.value));
  }, [templates]);

  const templatesFiltrados = useMemo(() => {
    if (categoriaFiltro === 'todas') return templates;
    return templates.filter(t => (t.categoria || 'geral') === categoriaFiltro);
  }, [templates, categoriaFiltro]);

  const catalogoFiltrado = useMemo(() => {
    if (categoriaFiltro === 'todas') return MODELOS_PRE_PRONTOS;
    return MODELOS_PRE_PRONTOS.filter(m => m.categoria === categoriaFiltro);
  }, [categoriaFiltro]);

  if (loading) {
    return (
      <Card className="rounded-2xl shadow-sm border border-gray-100 dark:border-[#383838] dark:bg-[#222529] overflow-hidden">
        <div className="flex flex-col items-center justify-center h-64 text-gray-400 dark:text-[#ABABAD]">
          <Iconify icon="mdi:loading" className="w-8 h-8 animate-spin mb-4 text-[#7C3AED]" />
          <p className="text-sm font-medium">Carregando modelos...</p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => setCategoriaFiltro('todas')}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${categoriaFiltro === 'todas' ? 'bg-[#7C3AED] text-white' : 'bg-gray-100 dark:bg-[#1A1D21] text-gray-600 dark:text-[#ABABAD] hover:bg-gray-200 dark:hover:bg-[#383838]'}`}
        >
          Todas
        </button>
        {categoriasDisponiveis.map(c => (
          <button
            key={c.value}
            onClick={() => setCategoriaFiltro(c.value)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${categoriaFiltro === c.value ? 'bg-[#7C3AED] text-white' : 'bg-gray-100 dark:bg-[#1A1D21] text-gray-600 dark:text-[#ABABAD] hover:bg-gray-200 dark:hover:bg-[#383838]'}`}
          >
            {c.label}
          </button>
        ))}
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-gray-900 dark:text-[#D1D2D3]">Meus Modelos</h3>
          {isAdmin && (
            <Button
              className="h-9 bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-medium rounded-lg px-4 text-xs"
              onClick={() => navigate('/dashboard/propostas/modelos/novo')}
            >
              <Iconify icon="eva:plus-fill" className="mr-2" width={16} />
              Novo Modelo
            </Button>
          )}
        </div>

        {templatesFiltrados.length === 0 ? (
          <p className="text-xs text-gray-400 py-6 text-center">Nenhum modelo nesta categoria ainda.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templatesFiltrados.map(template => (
              <Card key={template._id} className="rounded-2xl border border-gray-100 dark:border-[#383838] dark:bg-[#222529] overflow-hidden group">
                <MiniPreview temaCor={template.layout?.tema_cor} logoUrl={template.layout?.logo_url} />
                <div className="p-4">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-sm font-semibold text-gray-900 dark:text-[#D1D2D3] truncate">{template.nome}</span>
                    {template.padrao && (
                      <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md bg-violet-50 text-[#7C3AED] dark:bg-violet-500/10 flex-shrink-0">Padrão</span>
                    )}
                  </div>
                  <div className="mb-1.5"><CategoriaBadge categoria={template.categoria} /></div>
                  <p className="text-xs text-gray-500 dark:text-[#ABABAD] line-clamp-2 min-h-[32px]">{template.descricao || 'Sem descrição'}</p>

                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100 dark:border-[#383838]">
                    <button
                      onClick={() => handleUsarProprio(template)}
                      className="text-xs text-[#7C3AED] font-medium hover:underline flex items-center gap-1"
                    >
                      <Iconify icon="mdi:file-plus-outline" width={13} /> Usar
                    </button>
                    <button
                      onClick={() => setPreviewModelo(template)}
                      className="text-xs text-gray-500 dark:text-[#ABABAD] font-medium hover:text-[#7C3AED] flex items-center gap-1"
                    >
                      <Iconify icon="eva:eye-outline" width={13} /> Ver
                    </button>
                    {isAdmin && (
                      <>
                        <button
                          onClick={() => navigate(`/dashboard/propostas/modelos/${template._id}/editar`)}
                          className="text-xs text-gray-500 dark:text-[#ABABAD] font-medium hover:text-[#7C3AED] flex items-center gap-1"
                        >
                          <Iconify icon="eva:edit-2-outline" width={13} /> Editar
                        </button>
                        <button
                          onClick={() => handleDuplicar(template)}
                          className="text-xs text-gray-500 dark:text-[#ABABAD] font-medium hover:text-[#7C3AED] flex items-center gap-1"
                        >
                          <Iconify icon="mdi:content-copy" width={13} /> Duplicar
                        </button>
                        {!template.padrao && (
                          <button
                            onClick={() => handleExcluir(template)}
                            className="text-xs text-gray-400 hover:text-red-500 font-medium flex items-center gap-1 ml-auto"
                          >
                            <Iconify icon="mdi:trash-can-outline" width={13} />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="mb-3">
          <h3 className="text-sm font-bold text-gray-900 dark:text-[#D1D2D3] flex items-center gap-1.5">
            <Iconify icon="mdi:file-multiple-outline" width={16} className="text-[#7C3AED]" />
            Biblioteca de Modelos
          </h3>
          <p className="text-xs text-gray-400 mt-0.5">Modelos prontos para diferentes tipos de negócio. Use direto em uma proposta ou salve nos seus modelos.</p>
        </div>

        {catalogoFiltrado.length === 0 ? (
          <p className="text-xs text-gray-400 py-6 text-center">Nenhum modelo da biblioteca nesta categoria.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {catalogoFiltrado.map(modelo => (
              <Card key={modelo.id} className="rounded-2xl border border-gray-100 dark:border-[#383838] dark:bg-[#222529] overflow-hidden group">
                <MiniPreview temaCor={modelo.layout?.tema_cor} estilo={modelo.design_estilo} />
                <div className="p-4">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-sm font-semibold text-gray-900 dark:text-[#D1D2D3] truncate">{modelo.nome}</span>
                  </div>
                  <div className="mb-1.5"><CategoriaBadge categoria={modelo.categoria} /></div>
                  <p className="text-xs text-gray-500 dark:text-[#ABABAD] line-clamp-2 min-h-[32px]">{modelo.descricao}</p>

                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100 dark:border-[#383838]">
                    <button
                      onClick={() => handleUsarCatalogo(modelo)}
                      className="text-xs text-[#7C3AED] font-medium hover:underline flex items-center gap-1"
                    >
                      <Iconify icon="mdi:file-plus-outline" width={13} /> Usar modelo
                    </button>
                    <button
                      onClick={() => setPreviewModelo(modelo)}
                      className="text-xs text-gray-500 dark:text-[#ABABAD] font-medium hover:text-[#7C3AED] flex items-center gap-1"
                    >
                      <Iconify icon="eva:eye-outline" width={13} /> Ver
                    </button>
                    {isAdmin && (
                      <button
                        onClick={() => handleSalvarNaBiblioteca(modelo)}
                        className="text-xs text-gray-500 dark:text-[#ABABAD] font-medium hover:text-[#7C3AED] flex items-center gap-1 ml-auto"
                        title="Salvar uma cópia editável nos seus modelos"
                      >
                        <Iconify icon="mdi:content-save-outline" width={13} /> Salvar
                      </button>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Pré-visualização do modelo em tamanho real antes de usar */}
      <Dialog open={Boolean(previewModelo)} onOpenChange={(open) => { if (!open) setPreviewModelo(null); }}>
        <DialogContent className="max-w-[900px] w-[94vw] max-h-[92vh] p-0 gap-0 overflow-hidden flex flex-col">
          {previewModelo && (
            <>
              {/* pr-14: reserva o canto direito para o "X" do DialogContent, que é
                  posicionado por cima (absolute right-4 top-4) e cobria o botão */}
              <div className="flex items-center justify-between gap-3 pl-5 pr-14 py-3.5 border-b border-gray-100 dark:border-[#383838] flex-shrink-0">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="w-8 h-8 rounded-xl bg-violet-50 dark:bg-violet-500/10 flex items-center justify-center flex-shrink-0">
                    <Iconify icon="eva:eye-outline" width={16} className="text-[#7C3AED]" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 dark:text-[#D1D2D3] truncate">{previewModelo.nome}</p>
                    <p className="text-[11px] text-gray-400">Pré-visualização do modelo — dados de exemplo</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    const modelo = previewModelo;
                    setPreviewModelo(null);
                    if (modelo._id) handleUsarProprio(modelo);
                    else handleUsarCatalogo(modelo);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-[#7C3AED] hover:bg-[#6d28d9] text-white text-xs font-medium rounded-xl transition-all shadow-sm shadow-[#7C3AED]/20 active:transform active:scale-95 flex-shrink-0"
                >
                  <Iconify icon="mdi:file-plus-outline" width={14} />
                  Usar este modelo
                </button>
              </div>
              <div className="flex-1 min-h-0 overflow-auto bg-gray-100 dark:bg-[#1A1D21] py-6">
                {previewModelo.modo_design === 'canvas' && previewModelo.canvas?.paginas?.length ? (
                  <CanvasReadOnly proposta={propostaDePreview(previewModelo)} />
                ) : (
                  <div className="px-6">
                    <DocumentCanvas proposta={propostaDePreview(previewModelo)} onChange={() => {}} readOnly isModelo={false} />
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
